// Dashboard Home - Metrics overview and alerts
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, Timestamp, collectionGroup, doc, setDoc } from 'firebase/firestore';
import { seedDefaultChatRooms } from '../utils/seedDefaults';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Alert,
    AlertTitle,
    Chip,
    IconButton,
    LinearProgress
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import PeopleIcon from '@mui/icons-material/People';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ChatIcon from '@mui/icons-material/Chat';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FavoriteIcon from '@mui/icons-material/Favorite';

const COLORS = ['#FF6B9D', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];

const MetricCard = ({ title, value, subtitle, icon, color, trend, onClick }) => (
    <Card
        sx={{
            height: '100%',
            cursor: onClick ? 'pointer' : 'default',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: 4 } : {}
        }}
        onClick={onClick}
    >
        <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color={color}>
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                    {trend && (
                        <Chip
                            icon={<TrendingUpIcon fontSize="small" />}
                            label={trend}
                            size="small"
                            color="success"
                            sx={{ mt: 1 }}
                        />
                    )}
                </Box>
                <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: `${color}20`,
                    color: color
                }}>
                    {icon}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const Dashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState({
        activeUsers: 0,
        pendingReports: 0,
        todayMessages: 0,
        flaggedContent: 0,
        palsActiveUsers: 0
    });
    const [alerts, setAlerts] = useState([]);
    const [reportsByReason, setReportsByReason] = useState([]);
    const [activityTrend, setActivityTrend] = useState([]);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        const emptySnap = { size: 0, forEach: () => {} };
        const safe = async (fn, fallback) => {
            try {
                return await fn();
            } catch (error) {
                console.warn(error);
                return fallback;
            }
        };
        try {
            const fiveMinutesAgo = Timestamp.fromDate(new Date(Date.now() - 5 * 60 * 1000));
            const activeUsersSnapshot = await safe(
                () => getDocs(query(collection(db, 'users'), where('lastSeen', '>', fiveMinutesAgo))),
                emptySnap
            );

            const pendingReportsSnapshot = await safe(
                () => getDocs(query(collection(db, 'moderation_reports'), where('status', '==', 'pending'))),
                emptySnap
            );

            const flaggedSnapshot = await safe(
                () => getDocs(query(
                    collection(db, 'moderation_reports'),
                    where('status', '==', 'pending'),
                    where('reason', 'in', ['selfHarm', 'harmful', 'harassment'])
                )),
                emptySnap
            );

            // Build reports by reason for pie chart
            const reasonCounts = {};
            pendingReportsSnapshot.forEach(doc => {
                const reason = doc.data().reason || 'other';
                reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
            });
            const pieData = Object.entries(reasonCounts).map(([name, value]) => ({ name, value }));

            // Create alerts for high-severity items
            const alertsList = [];
            if (flaggedSnapshot.size > 0) {
                alertsList.push({
                    severity: 'error',
                    title: 'High Priority Reports',
                    message: `${flaggedSnapshot.size} reports require immediate attention (self-harm, harmful content)`
                });
            }

            // Count today's messages across all rooms via collectionGroup
            let todayMessages = 0;
            try {
                const startOfDay = Timestamp.fromDate(new Date(new Date().setHours(0, 0, 0, 0)));
                const todayMsgsSnap = await getDocs(
                    query(collectionGroup(db, 'messages'), where('createdAt', '>', startOfDay))
                );
                todayMessages = todayMsgsSnap.size;
            } catch (e) {
                // collectionGroup may not be indexed; fall back gracefully
                console.warn('Could not count today messages:', e.message);
            }

            // Count PALS room members (users who joined a PALS room)
            let palsActiveUsers = 0;
            try {
                const palsRoomsSnap = await getDocs(
                    query(collection(db, 'chat_rooms'), where('isPalsRoom', '==', true))
                );
                palsActiveUsers = palsRoomsSnap.docs.reduce((sum, d) => sum + (d.data().memberCount || 0), 0);
            } catch (e) {
                console.warn('Could not count PALS users:', e.message);
            }

            try {
                const communitiesSnap = await getDocs(collection(db, 'forum_communities'));
                if (communitiesSnap.empty) {
                    const defaults = [
                        { id: 'ttc_warriors', name: 'TTC Warriors', description: 'Support and tips for those trying to conceive.', emoji: '🌸', color: 0xFFE91E63, trackingModes: ['ttc', 'regular'] },
                        { id: 'pregnant_proud', name: 'Pregnant & Proud', description: 'Weekly updates, symptom sharing, and bump photos.', emoji: '🤰', color: 0xFF9C27B0, trackingModes: ['pregnancy'] },
                        { id: 'pcos_circle', name: 'PCOS Support Circle', description: 'Managing PCOS together with diet tips and emotional support.', emoji: '💪', color: 0xFF2196F3, trackingModes: ['regular', 'ttc', 'perimenopause'] },
                        { id: 'perimenopause_pioneers', name: 'Perimenopause Pioneers', description: 'Navigating the transition with symptoms, solutions, and sisterhood.', emoji: '🦋', color: 0xFFFF9800, trackingModes: ['perimenopause'] },
                        { id: 'cycle_syncing', name: 'Cycle Syncing Squad', description: 'Optimizing life with your cycle — nutrition, workouts, and more.', emoji: '🔄', color: 0xFF4CAF50, trackingModes: ['regular', 'ttc', 'perimenopause'] },
                        { id: 'pals_support', name: 'PALS Safe Space', description: 'A gentle, private space for pregnancy and infant loss support.', emoji: '💜', color: 0xFF7E57C2, trackingModes: ['pals', 'regular', 'ttc'], isPrivate: true, requiresApproval: true },
                        { id: 'first_timers', name: 'First-Time Moms', description: 'Questions, fears, and excitement about a first pregnancy.', emoji: '👶', color: 0xFFEC407A, trackingModes: ['pregnancy', 'ttc'] },
                        { id: 'self_care', name: 'Self-Care Sanctuary', description: 'Mental health, stress relief, and treating yourself.', emoji: '🧘', color: 0xFF26A69A, trackingModes: ['regular', 'ttc', 'pregnancy', 'pals', 'perimenopause'] },
                    ];
                    await Promise.all(defaults.map((community) =>
                        setDoc(doc(db, 'forum_communities', community.id), {
                            ...community,
                            memberCount: 0,
                            postCount: 0,
                            isPremiumOnly: false,
                            isPrivate: !!community.isPrivate,
                            requiresApproval: !!community.requiresApproval,
                            createdAt: Timestamp.fromDate(new Date(2024, 0, 1)),
                        })
                    ));
                }
            } catch (e) {
                console.warn('Could not seed forum communities:', e.message);
            }

            try {
                await seedDefaultChatRooms(db);
            } catch (e) {
                console.warn('Could not seed chat rooms:', e.message);
            }

            // Mock activity trend data (in production, aggregate from messages collection)
            const trendData = [
                { day: 'Mon', messages: 120, reports: 5 },
                { day: 'Tue', messages: 180, reports: 8 },
                { day: 'Wed', messages: 150, reports: 3 },
                { day: 'Thu', messages: 200, reports: 12 },
                { day: 'Fri', messages: 170, reports: 6 },
                { day: 'Sat', messages: 90, reports: 2 },
                { day: 'Sun', messages: 80, reports: 1 }
            ];

            setMetrics({
                activeUsers: activeUsersSnapshot.size,
                pendingReports: pendingReportsSnapshot.size,
                todayMessages,
                flaggedContent: flaggedSnapshot.size,
                palsActiveUsers
            });
            setAlerts(alertsList);
            setReportsByReason(pieData);
            setActivityTrend(trendData);

        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>Dashboard</Typography>
                <LinearProgress sx={{ mt: 2 }} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Community health overview and alerts
                </Typography>
            </Box>

            {/* Alerts */}
            {alerts.map((alert, index) => (
                <Alert
                    key={index}
                    severity={alert.severity}
                    sx={{ mb: 2 }}
                    action={
                        <IconButton size="small" onClick={() => navigate('/moderation')}>
                            <ArrowForwardIcon />
                        </IconButton>
                    }
                >
                    <AlertTitle>{alert.title}</AlertTitle>
                    {alert.message}
                </Alert>
            ))}

            {/* Metrics Grid */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        title="Active Users"
                        value={metrics.activeUsers}
                        subtitle="Online now"
                        icon={<PeopleIcon />}
                        color="#4ECDC4"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        title="Pending Reports"
                        value={metrics.pendingReports}
                        subtitle="Awaiting review"
                        icon={<ReportProblemIcon />}
                        color="#FF6B9D"
                        onClick={() => navigate('/moderation')}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        title="Messages Today"
                        value={metrics.todayMessages}
                        subtitle="Across all rooms"
                        icon={<ChatIcon />}
                        color="#45B7D1"
                        trend="+12%"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard
                        title="PALS Community"
                        value={metrics.palsActiveUsers}
                        subtitle="Active in support rooms"
                        icon={<FavoriteIcon />}
                        color="#96CEB4"
                    />
                </Grid>
            </Grid>

            {/* Charts Row */}
            <Grid container spacing={3}>
                {/* Activity Trend */}
                <Grid size={{ xs: 12, md: 8 }} sx={{ minWidth: 0 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Weekly Activity
                            </Typography>
                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                <Box sx={{ width: '100%', minWidth: 480, height: 320 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={activityTrend} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="day" />
                                        <YAxis yAxisId="left" width={40} domain={[0, 'auto']} />
                                        <YAxis yAxisId="right" orientation="right" width={40} domain={[0, 'auto']} />
                                        <Tooltip />
                                        <Line yAxisId="left" type="monotone" dataKey="messages" stroke="#4ECDC4" strokeWidth={2} name="Messages" />
                                        <Line yAxisId="right" type="monotone" dataKey="reports" stroke="#FF6B9D" strokeWidth={2} name="Reports" />
                                    </LineChart>
                                </ResponsiveContainer>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Reports by Reason */}
                <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 0 }}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Reports by Reason
                            </Typography>
                            {reportsByReason.length > 0 ? (
                                <Box sx={{ width: '100%', height: 280, minWidth: 220 }}>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie
                                                data={reportsByReason}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value }) => `${name}: ${value}`}
                                            >
                                                {reportsByReason.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260 }}>
                                    <Typography color="text.secondary">No pending reports</Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;
